import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Phone, Plus, Search, Bookmark, Filter, Home, Trash2, Square, CheckSquare } from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { formatEuro, contractProgress, daysSince, formatTimeSince } from "@/lib/format";
import QuickCallDialog from "@/components/QuickCallDialog";
import NewLeadDialog from "@/components/NewLeadDialog";
import LiveStatusBadge from "@/components/LiveStatusBadge";
import StatusChip from "@/components/StatusChip";
import ConfirmDialog from "@/components/ConfirmDialog";
import LeadBulkBar, { leadDeleteMessage } from "@/components/LeadBulkBar";
import BulkArchiveDialog from "@/components/BulkArchiveDialog";
import LeadFilterChips, { readFilterFromParams, writeFilterToParams, filterToApiParams } from "@/components/LeadFilterChips";
import { useLeadSelection } from "@/lib/useLeadSelection";

export default function Leads() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const postcode = params.get("postal_code") || "all";
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callLead, setCallLead] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [query, setQuery] = useState("");
  const filter = readFilterFromParams(params);
  const setFilter = (next) => setParams(writeFilterToParams(next, params));

  // Request-token om race conditions tussen achtereenvolgende fetches op
  // te vangen — alleen de laatst-gestarte fetch mag de UI overschrijven.
  const reqIdRef = useRef(0);

  const fetchLeads = async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    try {
      const r = await http.get("/api/leads", {
        params: { target_view: "leads_new", postal_code: postcode, ...filterToApiParams(filter) },
      });
      if (myId !== reqIdRef.current) return;
      setLeads(r.data);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  };
  useEffect(() => { setLeads([]); fetchLeads(); /* eslint-disable-next-line */ },
    [postcode, params.get("f_status"), params.get("f_live"), params.get("f_option"), params.get("f_pmin"), params.get("f_pmax")]);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter(l =>
      l.address?.toLowerCase().includes(q) ||
      l.postal_code?.includes(q) ||
      l.city?.toLowerCase().includes(q) ||
      l.agent_name?.toLowerCase().includes(q) ||
      l.owner_name?.toLowerCase().includes(q)
    );
  }, [leads, query]);

  const postcodes = useMemo(() => {
    const s = new Set(); leads.forEach(l => l.postal_code && s.add(l.postal_code));
    return Array.from(s).sort();
  }, [leads]);

  const openLead = (id) => {
    navigate(`/lead/${id}`, {
      state: { from: location.pathname + location.search, list: filtered.map(l => l.id) },
    });
  };

  const sel = useLeadSelection(fetchLeads);

  return (
    <div className="flex-1 flex min-w-0" data-testid="leads-screen">
      <div className="flex-1 min-w-0 p-8 overflow-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
              {t("nav.pipeline")}
            </div>
            <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">
              {t("leads.overview_title")}{postcode !== "all" ? ` — ${postcode}` : ""}
            </h1>
            <p className="text-[14px] text-[var(--ink-500)] mt-1">
              {t("leads.overview_sub", { count: filtered.length })}
            </p>
          </div>
          <button type="button" data-testid="btn-new-lead" className="btn btn-primary"
                  onClick={() => setShowNew(true)}>
            <Plus size={16} /> {t("leads.new_lead")}
          </button>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-400)]" />
            <input
              data-testid="leads-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`${t("common.search")}…`}
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm focus:border-[var(--teal-500)] outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-[var(--ink-400)]" />
            <select
              data-testid="leads-postcode-filter"
              value={postcode}
              onChange={(e) => {
                const n = new URLSearchParams(params);
                if (e.target.value === "all") n.delete("postal_code");
                else n.set("postal_code", e.target.value);
                setParams(n);
              }}
              className="h-10 px-3 pr-8 rounded-lg border border-[var(--border-soft)] bg-white text-sm outline-none focus:border-[var(--teal-500)]"
            >
              <option value="all">{t("common.all")} postcodes</option>
              {postcodes.map(pc => <option key={pc} value={pc}>{pc}</option>)}
            </select>
          </div>
        </div>

        <div className="mb-5">
          <LeadFilterChips value={filter} onChange={setFilter} sourceType="all" />
        </div>

        {/* List */}
        {loading ? (
          <div className="text-[var(--ink-500)] text-sm">{t("common.loading")}</div>
        ) : filtered.length === 0 ? (
          <EmptyLeads />
        ) : (
          <ul className="space-y-3" data-testid="leads-list">
            {filtered.map(lead => (
              <LeadRow
                key={lead.id}
                lead={lead}
                selected={sel.selected.has(lead.id)}
                onToggle={() => sel.toggleOne(lead.id)}
                onSelect={() => openLead(lead.id)}
                onCall={() => setCallLead(lead)}
                onDelete={() => sel.requestArchive([lead.id])}
              />
            ))}
          </ul>
        )}
      </div>

      <LeadBulkBar
        count={sel.selected.size}
        total={filtered.length}
        allSelected={filtered.length > 0 && filtered.every(l => sel.selected.has(l.id))}
        onToggleAll={() => sel.toggleAll(filtered.map(l => l.id))}
        onClear={sel.clear}
        onArchive={() => sel.requestArchive(Array.from(sel.selected))}
        onDelete={() => sel.requestDelete(Array.from(sel.selected))}
      />

      <BulkArchiveDialog sel={sel} />

      <ConfirmDialog
        open={sel.confirmIds !== null}
        danger
        title={(sel.confirmIds?.length || 0) > 1 ? "Leads definitief verwijderen" : "Lead definitief verwijderen"}
        message={leadDeleteMessage(sel.confirmIds)}
        confirmLabel="Definitief verwijderen"
        onConfirm={sel.doDelete}
        onCancel={sel.cancelDelete}
      />

      {callLead && (
        <QuickCallDialog
          lead={callLead}
          onClose={() => setCallLead(null)}
          onSaved={() => { setCallLead(null); fetchLeads(); }}
        />
      )}

      {showNew && (
        <NewLeadDialog
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); fetchLeads(); }}
        />
      )}
    </div>
  );
}

function LeadRow({ lead, onSelect, onCall, onDelete, selected, onToggle }) {
  const prog = contractProgress(lead.date_posted);
  const days = daysSince(lead.date_posted);
  const thumb = lead.photos?.[0];
  return (
    <li
      onClick={onSelect}
      data-testid={`lead-row-${lead.id}`}
      className={`card cursor-pointer !p-4 grid grid-cols-12 gap-4 items-center transition-all hover:border-[var(--border-strong)] ${selected ? "!border-[var(--teal-500)] bg-[var(--sand-50)]" : ""}`}
    >
      <div className="col-span-1 shrink-0 flex items-center gap-2">
        <button type="button"
                data-testid={`lead-select-${lead.id}`}
                onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
                className="grid place-items-center w-6 h-6 text-[var(--ink-400)] hover:text-[var(--teal-700)]"
                title={selected ? "Deselecteren" : "Selecteren"}>
          {selected ? <CheckSquare size={18} className="text-[var(--teal-700)]" /> : <Square size={18} />}
        </button>
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--sand-200)] grid place-items-center">
          {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                 : <Home size={16} className="text-[var(--ink-400)]" />}
        </div>
      </div>
      <div className="col-span-4 min-w-0">
        <div className="text-[15px] font-semibold text-[var(--ink-900)] truncate flex items-center gap-2 flex-wrap">
          <span className="truncate">{lead.address} · {lead.postal_code} {lead.city}</span>
          <StatusChip lead={lead} />
          <LiveStatusBadge lead={lead} />
          {lead.in_option === true && (
            <span className="chip !text-[10px] !py-[1px] bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"
                  data-testid={`in-option-chip-${lead.id}`}>
              In optie
            </span>
          )}
          {lead.source_type === "makelaar" && lead.was_particulier && (
            <span className="chip !text-[10px] !py-[1px] !px-1.5 bg-[var(--sand-100)]
                             text-[var(--ink-700)] border-[var(--border-soft)]"
                  title="Dit pand stond ooit als particuliere verkoop online"
                  data-testid={`was-particulier-chip-${lead.id}`}>
              ooit particulier
            </span>
          )}
        </div>
        <div className="text-[12px] text-[var(--ink-500)] mt-1 flex flex-wrap gap-x-3 gap-y-1">
          {lead.source_type === "makelaar" && lead.agent_name && (
            <>
              <span>{lead.agent_name}</span>
              <span className="text-[var(--ink-300)]">·</span>
            </>
          )}
          <span>Online {formatTimeSince(lead.date_posted)}</span>
        </div>
      </div>
      <div className="col-span-2 tabular">
        <div className="text-[15px] font-semibold text-[var(--ink-900)]">{formatEuro(lead.price)}</div>
        {lead.old_price > 0 && lead.old_price > (lead.price || 0) && (
          <div className="text-[12px] text-[var(--ink-400)] line-through">{formatEuro(lead.old_price)}</div>
        )}
      </div>
      <div className="col-span-3">
        <div className="timebar">
          <div className={`timebar-fill ${prog.tone === "warn" ? "warn" : prog.tone === "danger" ? "danger" : ""}`}
               style={{ width: `${prog.pct}%` }} />
        </div>
        <div className="flex justify-between mt-1.5 text-[11px] text-[var(--ink-500)] tabular">
          <span>{formatTimeSince(lead.date_posted)}</span>
          <span>{lead.call_count > 0 ? t("leads.calls_count", { n: lead.call_count }) : t("leads.not_called")}</span>
        </div>
      </div>
      <div className="col-span-2 flex justify-end items-center gap-2">
        {lead.shortlisted && <Bookmark size={16} className="text-[var(--teal-600)] fill-[var(--teal-500)]" />}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onCall(); }}
          data-testid={`btn-call-${lead.id}`}
          className="btn btn-ghost h-9 !px-3"
        >
          <Phone size={14} /> {t("leads.call")}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
          data-testid={`btn-delete-${lead.id}`}
          title="Lead verwijderen"
          className="p-2 rounded-md text-[#B43232] hover:bg-[#FBE7E7]"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </li>
  );
}

function EmptyLeads() {
  return (
    <div className="card text-center py-16 px-6" data-testid="leads-empty">
      <div className="w-14 h-14 mx-auto rounded-xl bg-[var(--teal-50)] text-[var(--teal-700)] grid place-items-center mb-4">
        <Search size={22} />
      </div>
      <h3 className="font-display text-[20px] text-[var(--ink-900)]">{t("leads.no_leads_title")}</h3>
      <p className="text-[14px] text-[var(--ink-500)] mt-2 max-w-md mx-auto">
        {t("leads.no_leads_body")}
      </p>
    </div>
  );
}
